import { FormDialog } from '@/components/FormDialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { isApiError } from '@/utils/error'
import { showError, showSuccess } from '@/utils/toast'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateBed, useGetBed, useUpdateBed } from '../hooks/useBed'

export function BedDialog({
  open,
  onOpenChange,
  departmentId,
  bedId,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  departmentId: string
  bedId?: string
}) {
  const { t } = useTranslation()
  const { data: bed, isLoading: isLoadingBed } = useGetBed(departmentId, bedId!, open)
  const createBed = useCreateBed()
  const updateBed = useUpdateBed()

  const [form, setForm] = useState({ name: '' })
  const isEdit = !!bedId
  const isInvalid = !form.name
  const isLoading = isLoadingBed || createBed.isPending || updateBed.isPending

  // const availableItems = items.filter(i => i.departmentId == bed?.departmentId && (i.status === 'available' || i.status === 'in-use'))
  useEffect(() => {
    if (bed) {
      setForm({
        name: bed.name,
      })
    } else {
      setForm({
        name: '',
      })
    }
  }, [bed, departmentId, open])

  function handleSubmit() {
    if (!form.name) return

    if (isInvalid) return
    const options = {
      onSuccess: () => {
        onOpenChange(false)
        showSuccess(isEdit ? t('bed.toast.editSuccess') : t('bed.toast.addSuccess'))
      },
      onError: (error: unknown) => {
        if (isApiError(error)) showError(error.message)
      },
    }

    const payload = {
      ...form,
      departmentId: departmentId,
    }
    if (isEdit && bed) {
      updateBed.mutate({ departmentId: departmentId, id: bedId, payload }, options)
    } else {
      createBed.mutate({ departmentId: departmentId, payload }, options)
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('bed.edit') : t('bed.add')}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      isInvalid={isInvalid}
      contentTestId="bed-dialog"
      submitTestId="bed-form-submit"
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5 col-span-2 sm:col-span-1">
          <Label className="text-xs">{t('common.bed')} *</Label>
          <Input
            value={form.name}
            data-testid="bed-form-name"
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder={t('bed.placeholder.name')}
          />
        </div>
      </div>
    </FormDialog>
  )
}
